/** Always-available skill IDs for anonymous / expired free tier (no login). */
export const ALWAYS_AVAILABLE_SKILL_IDS = new Set([
  // Summer Prep
  10,
  20, 25, 30, 40,
  100, 110, 120, 130, 140, 150,
  200, 210, 220, 230, 240,
  300, 310,
  401, 402, 402, 404,
  431, 432, 433, 434, 435, 436, 437, 438, 439, 
  471, 472, 473,
  490,
  // Temporary: all live limits skills are free while the unit is being tested.
  1110, 1120, 1130, 1140, 1150, 1160, 1170, 1210, 1220, 1240, 1250, 1180, 1230,
  1310, 1320, 1330, 1410, 1420, 1510,
  2000,
  2011, 2012, 2013, 2014,
  3110,
  // Power Rule
  211, 212, 213, 214,
  221, 222, 223, 224,
  231, 232, 233, 234,
  241, 242, 243, 244,
  2002, 2004, 2006, 2010,
  2050, 2080,
  2110, 2120,
  // Integration
  3110, 3111, 3112, 3113, 3114, 3115,
  3120, 3130, 3150, 3210, 3220, 3230,

]);

/** Signed-in users can play these even after the trial ends; guests cannot. */
export const LOGIN_REQUIRED_FREE_SKILL_IDS = new Set([
  2110,
  2120,
]);

const SIGNED_IN_TIERS = new Set(['trial', 'active', 'expired']);

/**
 * @param {number|string|null|undefined} skillId
 */
export function isAlwaysAvailableSkill(skillId) {
  const id = Number(skillId);
  return Number.isFinite(id) && ALWAYS_AVAILABLE_SKILL_IDS.has(id);
}

/**
 * @param {number|string|null|undefined} skillId
 */
export function isLoginRequiredFreeSkill(skillId) {
  const id = Number(skillId);
  return Number.isFinite(id) && LOGIN_REQUIRED_FREE_SKILL_IDS.has(id);
}

function hasSignedInAccess(access) {
  return SIGNED_IN_TIERS.has(access?.tier);
}

/**
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 * @param {number|string|null|undefined} skillId
 */
export function canAccessSkillWithState(access, skillId) {
  if (access?.canAccessAll) {
    return true;
  }
  if (isAlwaysAvailableSkill(skillId)) {
    return true;
  }
  return hasSignedInAccess(access) && isLoginRequiredFreeSkill(skillId);
}

/**
 * @param {Array<{ skillId: number }>} skillGroup
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 */
export function isAnySkillInGroupAccessible(skillGroup, access) {
  if (access?.canAccessAll) {
    return true;
  }
  return skillGroup.some((skill) => canAccessSkillWithState(access, skill.skillId));
}

/**
 * @param {string} unit
 * @param {string} topic
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 * @param {Array<{ unit: string, topic: string, skillId: number, isLive?: boolean }>} registry
 */
export function topicHasAccessibleSkill(unit, topic, access, registry) {
  if (access?.canAccessAll) {
    return true;
  }
  return registry.some(
    (item) => item.unit === unit
      && item.topic === topic
      && item.isLive !== false
      && canAccessSkillWithState(access, item.skillId)
  );
}

/**
 * @param {{ skillId: number, isGrouped?: boolean, skillGroup?: Array<{ skillId: number }> }} skillData
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 */
export function isSkillEntryAccessible(skillData, access) {
  if (access?.canAccessAll) {
    return true;
  }
  if (skillData.isGrouped && skillData.skillGroup?.length) {
    return isAnySkillInGroupAccessible(skillData.skillGroup, access);
  }
  return canAccessSkillWithState(access, skillData.skillId);
}

/**
 * @param {{ skillId: number }} subskill
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 */
export function isSubskillAccessible(subskill, access) {
  return canAccessSkillWithState(access, subskill.skillId);
}

/**
 * Premium features (review cards, The Midway) require full access — no free tier.
 * @param {{ canAccessAll?: boolean, tier?: string }|null|undefined} access
 */
export function canAccessPremiumFeatures(access) {
  return Boolean(access?.canAccessAll);
}
